import { useMemo } from 'react';
import { useStore } from '../lib/store.js';
import { EditableTitle } from './EditableTitle.jsx';

export function V2Breadcrumb({ channelID, threadURN }) {
  const activeBranchId = useStore(s => {
    const raw = s.branchesByChannel[channelID]?.currentByThread?.get(threadURN) ?? null;
    return typeof raw === 'string' ? raw : null;
  });
  // Read branches map by reference — changes when any branch mutates (immutable updates).
  // Compute the breadcrumb in useMemo so we don't call selectBranchBreadcrumb in a
  // selector (it returns a new array every call, causing unnecessary re-renders).
  const channelEntry = useStore(s => s.branchesByChannel[channelID]);
  const breadcrumb = useMemo(() => {
    if (!activeBranchId || !channelEntry?.branches) return [];
    const crumbs = [];
    let id = activeBranchId;
    while (id && crumbs.length < 50) {
      const rec = channelEntry.branches.get(id);
      if (!rec) break;
      crumbs.unshift(rec);
      id = rec.parentBranchId ?? null;
    }
    return crumbs;
  }, [channelEntry, activeBranchId]);

  const renameBranch = useStore(s => s.renameBranch);
  const selectBranch = useStore(s => s.selectBranch);

  if (!activeBranchId) {
    return <span className="v2-breadcrumb" data-testid="v2-breadcrumb"><span className="v2-crumb-main">main</span></span>;
  }

  const leaf = breadcrumb[breadcrumb.length - 1];
  if (!leaf) {
    // Stale state during branch transition — show minimal breadcrumb without editable leaf.
    return (
      <span className="v2-breadcrumb" data-testid="v2-breadcrumb">
        <button
          type="button"
          className="v2-crumb-main"
          data-testid="v2-crumb-main"
          onClick={() => selectBranch({ channelID, threadURN, branchId: null })}
        >
          main
        </button>
        <span className="v2-crumb-sep"> → </span>
        <span className="v2-crumb-leaf">{activeBranchId}</span>
      </span>
    );
  }

  const leafTitle = leaf.userTitle ?? leaf.archivistTitle ?? leaf.title ?? activeBranchId;

  return (
    <span className="v2-breadcrumb" data-testid="v2-breadcrumb">
      <button
        type="button"
        className="v2-crumb-main"
        data-testid="v2-crumb-main"
        onClick={() => selectBranch({ channelID, threadURN, branchId: null })}
      >
        main
      </button>
      {breadcrumb.slice(0, -1).map(crumb => {
        const label = crumb.userTitle ?? crumb.archivistTitle ?? crumb.title ?? crumb.id;
        return (
          <span key={crumb.id} className="v2-crumb-sep-and-label">
            <span className="v2-crumb-sep"> → </span>
            <button
              type="button"
              className="v2-crumb-item"
              onClick={() => selectBranch({ channelID, threadURN, branchId: crumb.id })}
            >
              {label}
            </button>
          </span>
        );
      })}
      <span className="v2-crumb-sep"> → </span>
      <EditableTitle
        value={leafTitle}
        onCommit={title => renameBranch({ channelID, branchId: activeBranchId, title })}
        className="v2-crumb-leaf"
      />
    </span>
  );
}
